import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Box, Link, Table, Tooltip, Typography } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import { Link as RouterLink, useNavigate, useParams } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ApiCompetitionRouteCustomScoringConfig,
  ApiCustomScoringRoutePointsConfig,
} from "griptonite-api-client";
import { useCallback, useMemo } from "react";

import Warning from "@/assets/icons/Warning";
import { Route } from "@/core/types";
import {
  useCompetitionQuery,
  useCompetitionRouteCustomScoringConfigsByUuid,
  useCompetitionRouteCustomScoringConfigsQuery,
  useCurrentCompetitionUuid,
} from "@/hooks/competitions";
import { useCompetitionRoutesQuery } from "@/hooks/routes";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { useCompetitionContext } from "../CompetitionOverview/CompetitionProvider";
import { useModal } from "../modals/useModal";
import { RouteCircle } from "../RouteCircle";
import { RouteNameCell } from "../RoutesTable/RouteName";

const columnHelper = createColumnHelper<
  Route & { customScoringConfig?: ApiCustomScoringRoutePointsConfig }
>();

const emptyArray: Route[] = [];

const NumberWarning = ({ sx }: { sx?: SxProps }) => {
  return (
    <Tooltip
      title={
        <>
          <strong>No number assigned</strong>
        </>
      }
      size="sm"
      placement="bottom-start"
    >
      <Warning sx={sx} size="sm" />
    </Tooltip>
  );
};

const useCustomScoreColumns = (competitionUuid?: string) => {
  const { data: competition } = useCompetitionQuery(competitionUuid);
  const { data: customScoreConfigs } = useCompetitionRouteCustomScoringConfigsQuery(
    competitionUuid,
    useCallback((configs: ApiCompetitionRouteCustomScoringConfig[]) => {
      return configs.map((config) => config.config);
    }, []),
  );
  const baseConfig = competition?.scoringSystemSettings.baseRoutePointsConfig;

  return useMemo(() => {
    if (!competition || !customScoreConfigs || !baseConfig) {
      return [] as never[];
    }
    // We need to find a set of columns that represent all the custom scoring configs.
    // E.g. if the base config is Success 1 Try, Z 1 Try, Z2 1 Try, Success 2+ tries,
    // and a route config has these values for 1, 2 and 3 tries, then there should be 9 columns.
    const colIds = new Set<string>();
    if (competition?.scoringSystemSettings.failedAttemptPenalty) {
      colIds.add("successPoints_1");
      if (competition?.scoringSystemSettings.segmentsCount) {
        colIds.add("segment1Points_1");
        if (competition?.scoringSystemSettings.segmentsCount > 1) {
          colIds.add("segment2Points_1");
        }
      }
    } else {
      [baseConfig, ...customScoreConfigs].forEach((config) => {
        config.successPoints.forEach((_, index) => {
          colIds.add(`successPoints_${index + 1}`);
        });
        if (competition?.scoringSystemSettings.segmentsCount && config.segment1Points) {
          config.segment1Points.forEach((_, index) => {
            colIds.add(`segment1Points_${index + 1}`);
          });
          if (competition?.scoringSystemSettings.segmentsCount > 1 && config.segment2Points) {
            config.segment2Points.forEach((_, index) => {
              colIds.add(`segment2Points_${index + 1}`);
            });
          }
        }
      });
    }

    return Array.from(colIds, (colId) => {
      const [internalName, tries] = colId.split("_");
      const baseName = {
        successPoints: "Top",
        segment1Points: "Z1",
        segment2Points: "Z2",
      }[internalName];
      const name = `${baseName} - ${tries} ${tries === "1" ? "Try" : "Tries"} pts`;

      return columnHelper.accessor(
        (route) => {
          const pointsAr =
            route.customScoringConfig?.[internalName as keyof ApiCustomScoringRoutePointsConfig];
          if (!pointsAr) {
            return undefined;
          }
          const index = Math.min(parseInt(tries, 10) - 1, pointsAr.length - 1);
          return pointsAr[index];
        },
        {
          id: colId,
          header: name,
          size: 130,
          enableSorting: true,
          cell: (props) => {
            const v = props.getValue();
            return `${v}pt${v !== 1 ? "s" : ""}`;
          },
        },
      );
    });
  }, [baseConfig, competition, customScoreConfigs]);
};

export default function CompetitionRoutesTable() {
  const currentCompetitionUuid = useCurrentCompetitionUuid();
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: competition } = useCompetitionQuery(currentCompetitionUuid, currentVenueUuid);
  const { selectedCategoryUuid, categoryEditorIsDirty } = useCompetitionContext();
  const { data: routes, isPending } = useCompetitionRoutesQuery(
    currentCompetitionUuid,
    currentVenueUuid,
  );
  const customScoringConfigsByRouteUuid =
    useCompetitionRouteCustomScoringConfigsByUuid(currentCompetitionUuid);

  const routesWithCustomScoringPoints = useMemo(() => {
    return (
      routes?.map((route) => ({
        ...route,
        customScoringConfig:
          customScoringConfigsByRouteUuid?.[route.uuid] ||
          competition?.scoringSystemSettings.baseRoutePointsConfig,
      })) ?? []
    );
  }, [
    routes,
    customScoringConfigsByRouteUuid,
    competition?.scoringSystemSettings.baseRoutePointsConfig,
  ]);

  const filteredRoutes = useMemo(() => {
    if (!selectedCategoryUuid) {
      return routesWithCustomScoringPoints;
    }
    return routesWithCustomScoringPoints?.filter((route) =>
      route.competitionRouteGroups.some((crg) => crg.categories.includes(selectedCategoryUuid)),
    );
  }, [routesWithCustomScoringPoints, selectedCategoryUuid]);

  const compHasZones = !!(
    competition?.scoringSystemSettings.segment ||
    competition?.scoringSystemSettings.segmentsCount ||
    (competition &&
      ["IFSC_RANKING", "OLYMPICS_2024", "IFSC_2025"].includes(competition.scoringSystem))
  );

  const compHasTwoZones =
    competition?.scoringSystemSettings.segmentsCount === 2 ||
    competition?.scoringSystemSettings.segment2 ||
    competition?.scoringSystem === "OLYMPICS_2024";

  const customScoreColumns = useCustomScoreColumns(currentCompetitionUuid);

  const columnDefs = useMemo(() => {
    const categories = competition?.stages[0].categories || [];

    const resultsColumns =
      competition?.scoringSystem === "IFSC_RANKING"
        ? [
            columnHelper.accessor("successesCount", {
              id: "successes",
              header: "Tops",
              size: 80,
              enableSorting: true,
            }),
            columnHelper.accessor("zonesCount", {
              id: "zones",
              header: "Zones",
              size: 80,
              enableSorting: true,
            }),
            columnHelper.accessor("attemptsCount", {
              id: "attempts",
              header: "Tries",
              size: 80,
              enableSorting: true,
            }),
          ]
        : [
            columnHelper.accessor("flashesCount", {
              id: "flashes",
              header: "Flashes",
              size: 94,
              enableSorting: true,
            }),
            columnHelper.accessor("successesCount", {
              id: "successes",
              header: "Ticks",
              size: 80,
              enableSorting: true,
            }),
            columnHelper.accessor("attemptsCount", {
              id: "attempts",
              header: "Tries",
              size: 80,
              enableSorting: true,
            }),
            ...(compHasZones
              ? [
                  columnHelper.accessor("zonesCount", {
                    id: "zones",
                    header: compHasTwoZones ? "Zone 1" : "Zones",
                    size: 80,
                    enableSorting: true,
                  }),
                  ...(compHasTwoZones
                    ? [
                        columnHelper.accessor("zone2Count", {
                          id: "zones2",
                          header: "Zone 2",
                          size: 80,
                          enableSorting: true,
                        }),
                      ]
                    : []),
                ]
              : []),
            ...(competition?.scoringSystem === "CUSTOM" ? customScoreColumns : []),
          ];

    return [
      columnHelper.accessor("uuid", {
        id: "uuid",
        header: "Route",
        cell:
          categories.length === 1 || !competition?.settings?.hasDynamicRouteNumbering
            ? (props) => <RouteNameCell route={props.row.original} showTagWarning={false} />
            : (props) => (
                <RouteCircle
                  climbType={props.row.original.climbType}
                  colors={(props.row.original.colors || "#FFFFFF").split(":")}
                />
              ),
        size:
          categories.length === 1 || !competition?.settings?.hasDynamicRouteNumbering ? 120 : 80,
        sortDescFirst: false,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: (rowA, rowB) => {
          return (rowA.original.circuitNumber || 0) - (rowB.original.circuitNumber || 0);
        },
      }),
      ...(categories.length > 1 && (competition?.routeGroups.length || 0) > 1
        ? categories.map((category) =>
            columnHelper.accessor(
              (route) => {
                const routeGroup = route.competitionRouteGroups.find((x) =>
                  x.categories.includes(category.uuid),
                );
                if (!routeGroup) {
                  return undefined;
                }
                return (
                  route.displayRouteGroupMapping[routeGroup.uuid]?.number ??
                  (competition?.settings?.hasDynamicRouteNumbering
                    ? undefined
                    : route.circuitNumber)
                );
              },
              {
                sortUndefined: "last",
                id: "category-number-" + category.uuid,
                header:
                  category.name + (competition?.settings?.hasDynamicRouteNumbering ? " #" : ""),
                size: 120,
                enableSorting: true,
                cell: (props) => {
                  const val = props.getValue();
                  if (
                    !props.row.original.competitionRouteGroups.some((x) =>
                      x.categories.includes(category.uuid),
                    )
                  ) {
                    return "–";
                  }
                  if (!competition?.settings?.hasDynamicRouteNumbering) {
                    return "✅";
                  }
                  if (val === undefined) {
                    return (
                      <>
                        <NumberWarning />
                      </>
                    );
                  }
                  return val;
                },
              },
            ),
          )
        : []),
      ...resultsColumns,
    ];
  }, [competition, compHasZones, customScoreColumns]);

  const table = useReactTable({
    getRowId: (row) => row.uuid,
    columns: columnDefs,
    data: filteredRoutes || emptyArray,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: "uuid", desc: false }],
    },
  });

  const activeRouteUuid = useParams({ strict: false }).routeUuid;
  const { display: displayModal } = useModal();

  const navigate = useNavigate();
  const onRowClick = (uuid: string) => {
    if (!categoryEditorIsDirty) {
      navigate({
        from: "/gym/$venueUuid/events/$competitionUuid/routes",
        to: "./$routeUuid",
        params: (prev) => ({ ...prev, routeUuid: uuid }),
        replace: true,
      });
    } else {
      displayModal("UNSAVED_COMPETITION_CATEGORY_CHANGES_PROMPT");
    }
  };

  if (!isPending && competition && !routes?.length) {
    return (
      <Typography level="body-md" sx={{ margin: 1 }}>
        Looks like this competition doesn’t have any routes yet.{" "}
        <RouterLink
          to="/gym/$venueUuid/routes/$climbType"
          from="/gym/$venueUuid/events/$competitionUuid/routes"
          params={(prev) => ({
            venueUuid: prev.venueUuid,
            climbType: competition.climbType,
          })}
        >
          Go to the routes screen to create a route!
        </RouterLink>
      </Typography>
    );
  }

  return (
    <Table
      borderAxis="bothBetween"
      sx={{
        th: {
          backgroundColor: "neutral.100",
        },
        "td, th": {
          paddingX: 1,
          paddingY: 1,
          alignItems: "center",
          height: 40,
          display: "flex",
        },
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        // minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} style={{ display: "flex" }}>
            {headerGroup.headers.map((header) => {
              const isSorted = header.column.getIsSorted();
              return (
                <th
                  style={{
                    width: header.getSize(),
                    flexShrink: 0,
                  }}
                  key={header.id}
                >
                  <Link
                    underline="none"
                    textColor={header.column.getIsSorted() ? "primary.500" : "neutral.800"}
                    onClick={header.column.getToggleSortingHandler()}
                    startDecorator={
                      isSorted ? isSorted === "desc" ? <ArrowDownward /> : <ArrowUpward /> : null
                    }
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        // direction: header.column.id.startsWith(
                        //   "category-number-"
                        // )
                        //   ? "rtl"
                        //   : "ltr",
                        direction: "rtl",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box sx={{ direction: "ltr", display: "inline" }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}{" "}
                      </Box>
                    </Box>
                  </Link>
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => {
          return (
            <tr
              key={row.id}
              style={{
                display: "flex",
                width: "100%",
                backgroundColor:
                  row.id === activeRouteUuid ? "var(--joy-palette-primary-softBg)" : "white",
              }}
              onClick={() => onRowClick(row.id)}
            >
              {row.getVisibleCells().map((cell) => (
                <td style={{ width: cell.column.getSize(), flexShrink: 0 }} key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
